import { Fragment } from "react";

import { Button, Grid, Stack, Typography } from "@mui/material";
import { AttachFile } from "@mui/icons-material";

import _ from "lodash";

import SectionPreview from "../../../common/components/SectionPreview";
import { dateToStringShort, dateToStringLongSuffix } from "../../../common/utils/dateTime";
import { translate } from "../../../common/utils/formatters";
import { apiFileDownloadEndpoint } from "../../../common/components/mui-extended/DropzoneExtended";
import { VehicleContractPreviewDto } from "../../../interfaces/VehicleContract";

type VehicleContractProps = {
  model?: VehicleContractPreviewDto;
};

const VehicleContract = ({ model }: VehicleContractProps) => {
  const renderVehicleContractData = () => (
    <>
      <Typography>
        Договор за прехвърляне на МПС{" "}
        {model?.registrationNumber && (
          <strong>{`${model?.registrationNumber} / ${dateToStringShort(model?.issueDate)}`}</strong>
        )}
      </Typography>
      <Typography>
        издаден от нотариус <strong>{model?.notary?.name}</strong>, номер{" "}
        <strong>{_.padStart(model?.notary?.number?.toString(), 3, "0")}</strong>, район на действие{" "}
        <strong>{model?.notary?.settlementName}.</strong>
      </Typography>
    </>
  );

  const render = () => {
    return (
      <Stack spacing={3}>
        <Typography variant="subtitle2">
          Дата на въвеждане в регистъра: {dateToStringLongSuffix(model?.createDate)}
        </Typography>
        <SectionPreview title="Данни за договор">{renderVehicleContractData()}</SectionPreview>
        {model?.file?.blobId && (
          <SectionPreview title="Сканирано копие">
            <Button
              startIcon={<AttachFile />}
              variant="outlined"
              href={`${apiFileDownloadEndpoint}/${model?.file?.blobId}`}
            >
              {model?.file?.name}
            </Button>
          </SectionPreview>
        )}
        <SectionPreview title="Данни за МПС">{Application.renderVehicleData(model?.application)}</SectionPreview>
        <SectionPreview title="Данни за продавача">
          {Application.renderOwnersData(model?.application?.oldOwners)}
        </SectionPreview>
        <SectionPreview title="Данни за купувача">
          {Application.renderOwnersData(model?.application?.newOwners)}{" "}
        </SectionPreview>
      </Stack>
    );
  };

  return model ? render() : null;
};

export default VehicleContract;

const renderPersonData = (person: any) => (
  <Fragment key={person.egn}>
    <Stack spacing={1}>
      <Typography>Данни за физическо лице</Typography>
      <Grid container rowSpacing={1} columnSpacing={2}>
        <Grid item xs={12} md={4}>
          <Typography>Първо име</Typography>
          <Typography>
            <strong>{person.firstName}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography>Бащино име</Typography>
          <Typography>
            <strong>{person.middleName}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography>Фамилно име</Typography>
          <Typography>
            <strong>{person.lastName}</strong>
          </Typography>
        </Grid>
        {person.egn && (
          <Grid item xs={12} md={4}>
            <Typography>ЕГН</Typography>
            <Typography>
              <strong>{person.egn}</strong>
            </Typography>
          </Grid>
        )}
        {person.lnCh && (
          <Grid item xs={12} md={4}>
            <Typography>ЛНЧ/ЛН</Typography>
            <Typography>
              <strong>{person.lnCh}</strong>
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={8} />
        <Grid item xs={12} md={4}>
          <Typography>Семейно положение</Typography>
          <Typography>
            <strong>{getMaritalStatus(person.maritalStatus)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography>Постоянен адрес</Typography>
          <Typography>
            <strong>{person.permanentAddress}</strong>
          </Typography>
        </Grid>
      </Grid>
    </Stack>
    <Stack spacing={1}>
      <Typography>Документ за самоличност</Typography>
      <Grid container rowSpacing={1} columnSpacing={2}>
        <Grid item xs={12} md={4}>
          <Typography>Вид на документ</Typography>
          <Typography>
            <strong>{person.identityDocument?.documentType}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography>Име на структура на МВР – издала документа</Typography>
          <Typography>
            <strong>{person.identityDocument?.issuer}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography>Номер на документ</Typography>
          <Typography>
            <strong>{person.identityDocument?.number}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography>Дата на издаване</Typography>
          <Typography>
            <strong>{dateToStringShort(person.identityDocument?.issueDate)}</strong>
          </Typography>
        </Grid>
      </Grid>
    </Stack>
  </Fragment>
);

const renderEntityData = (entity: any) => (
  <Fragment key={entity.name}>
    <Stack spacing={1}>
      <Typography>Данни за юридическо лице</Typography>
      <Grid container rowSpacing={1} columnSpacing={2}>
        <Grid item xs={12} md={4}>
          <Typography>Наименование</Typography>
          <Typography>
            <strong>{entity.name}</strong>
          </Typography>
        </Grid>
        {entity.uic && (
          <Grid item xs={12} md={8}>
            <Typography>ЕИК</Typography>
            <Typography>
              <strong>{entity.uic}</strong>
            </Typography>
          </Grid>
        )}
        {entity.bulstat && (
          <Grid item xs={12} md={8}>
            <Typography>Булстат</Typography>
            <Typography>
              <strong>{entity.bulstat}</strong>
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} md={12}>
          <Typography>Адрес на управление</Typography>
          <Typography>
            <strong>{entity.managmentAddress}</strong>
          </Typography>
        </Grid>
      </Grid>
    </Stack>
  </Fragment>
);

export const Application = {
  renderVehicleData: (application: any) => (
    <Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography>Регистрационен номер на ППС</Typography>
          <Typography>
            <strong>{application?.vehicleData.registrationNumber}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>Рама на ППС</Typography>
          <Typography>
            <strong>{application?.vehicleData.vehicleIdentificationNumber}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>Номер на двигател</Typography>
          <Typography>
            <strong>{application?.vehicleData.engineNumber}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>Вид на СРМПС</Typography>
          <Typography>
            <strong>{getVehicleDocumentType(application?.vehicleData.documentType)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>Номер на СРМПС</Typography>
          <Typography>
            <strong>{application?.vehicleData.documentNumber}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>Дата на първа регистрация</Typography>
          <Typography>
            <strong>{dateToStringShort(application?.vehicleData.firstRegistrationDate)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>Платена гражданска отговорност</Typography>
          <Typography>
            <strong>{translate(application?.hasCivilLiability)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>Преминат технически преглед</Typography>
          <Typography>
            <strong>{translate(application?.hasAnnualTechnicalInspection)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography>Заплатен годишен данък</Typography>
          <Typography>
            <strong>{translate(application?.hasPaidLocalTaxes)}</strong>
          </Typography>
        </Grid>
      </Grid>
    </Stack>
  ),
  renderOwnersData: (owners: any) => (
    <>
      {owners && (
        <Stack spacing={2}>
          {owners?.personDataCollection?.map((person: any) => renderPersonData(person))}
          {owners?.entityDataCollection?.map((entity: any) => renderEntityData(entity))}
        </Stack>
      )}
    </>
  ),
};

const getOwners = (owners: any): string => {
  let ownersData = `${
    owners?.personDataCollection
      ? `
    Данни за физическо лице`
      : ""
  }`;
  owners?.personDataCollection.map((person: any) => (ownersData += getPersonData(person)));

  ownersData += `${
    owners?.entityDataCollection
      ? `
    Данни за юридичско лице`
      : ""
  }`;
  owners?.entityDataCollection.map((entity: any) => (ownersData += getEntityData(entity)));

  return ownersData;
};

const getPersonData = (person: any): string => {
  let personData = `
    Първо име: ${person.firstName}${person.middleName ? ", Бащино име: " + person.middleName : ""}, Фамилно име: ${
    person.lastName
  }${person.egn ? ", ЕГН: " + person.egn : ""}${
    person.lnCh ? ", ЛНЧ/ЛН: " + person.lnCh : ""
  }, Семейно положение: ${getMaritalStatus(person.maritalStatus)}, Постоянен адрес: ${person.permanentAddress}`;

  const identityDocument = person.identityDocument;
  if (identityDocument) {
    personData += `
    Документ за самоличност
    Вид документ: ${identityDocument.documentType}, Име на структура на МВР – издала документа: ${
      identityDocument.issuer
    }, Номер на документ: ${identityDocument.number}, Дата на издаване: ${dateToStringShort(identityDocument.issueDate)}
    `;
  }

  return personData;
};

const getEntityData = (entity: any): string => {
  return `
    Наименование: ${entity.name}${entity.uic ? ", ЕИК: " + entity.uic : ""}${
    entity.bulstat ? ", Булстат: " + entity.bulstat : ""
  }, Адрес на управление: ${entity.managmentAddress}
  `;
};

const getVehicleData = (application: any): string => {
  return `Данни за МПС
    Регистрационен номер на ППС: ${application?.vehicleData.registrationNumber}, Рама на ППС: ${
    application?.vehicleData.vehicleIdentificationNumber
  }${
    application?.vehicleData.engineNumber ? ", Номер на двигател: " + application.vehicleData.engineNumber : ""
  }, Вид на СРМПС: ${getVehicleDocumentType(application?.vehicleData.documentType)}, Номер на СРМПС:${
    application?.vehicleData.documentNumber
  }${
    application?.vehicleData.firstRegistrationDate
      ? ", Дата на първа регистрация: " + dateToStringShort(application.vehicleData.firstRegistrationDate)
      : ""
  }, Платена гражданска отговорност: ${translate(
    application?.hasCivilLiability
  )}, Преминат технически преглед: ${translate(
    application?.hasAnnualTechnicalInspection
  )}, Заплатен годишен данък: ${translate(application?.hasPaidLocalTaxes)}`;
};

export const ApplicationClipboard = {
  getText: (application: any): string => {
    let oldOwnersData = `Данни за продавача`;
    oldOwnersData += getOwners(application?.oldOwners);

    let newOwnersData = `Данни за купувача`;
    newOwnersData += getOwners(application?.newOwners);

    let vehicleData = getVehicleData(application);

    var applicationData = [oldOwnersData, newOwnersData, vehicleData].join(`\n\n`);
    return applicationData;
  },
};

const getMaritalStatus = (maritalStatus: any): string => {
  let status = "";
  switch (maritalStatus?.toUpperCase()) {
    case "ВДОВЕЦ_ВДОВИЦА": {
      status = "ВДОВЕЦ/ВДОВИЦА";
      break;
    }
    case "НЕЖЕНЕН_НЕОМЪЖЕНА": {
      status = "НЕЖЕНЕН/НЕОМЪЖЕНА";
      break;
    }
    case "ЖЕНЕН_ОМЪЖЕНА": {
      status = "ЖЕНЕН/ОМЪЖЕНА";
      break;
    }
    case "РАЗВЕДЕН_А": {
      status = "РАЗВЕДЕН(А)";
      break;
    }
    case "ФАКТ_РАЗДЕЛЕН_А": {
      status = "ФАКТ.РАЗДЕЛЕН(А)";
      break;
    }
    case "НЕПОКАЗАНО": {
      status = "НЕПОКАЗАНО";
      break;
    }
    default:
      break;
  }

  return status;
};

const getVehicleDocumentType = (vehicleDocumentType: any): string => {
  let documentType = "";
  switch (vehicleDocumentType?.toUpperCase()) {
    case "СР_НА_МПС": {
      documentType = "СР на МПС";
      break;
    }
    case "РЕГИСТРАЦИОНЕН_ТАЛОН": {
      documentType = "Регистрационен талон";
      break;
    }
    default:
      break;
  }

  return documentType;
};
